import { useEffect, useRef, useState } from "react";
import { Button } from "semantic-ui-react";
import {
  configAuth,
  addPolygon,
  addPolyline,
  addSimpleMarker,
  createMapView,
  createSketchView,
  createWebMap,
  MapItem,
} from "../../utils/arcgisUtils";
import { loadModules, loadCss } from "esri-loader";
import { ArcGisMapConfig } from "../../models/ArcGisMapConfig";

type Props = {
  mapConfig: ArcGisMapConfig;
  webMapItemId: string | null | undefined;
  center?: __esri.Point;
  items?: MapItem[];
  showLegend?: boolean;
  showLayersList?: boolean;
  onPopupClick?: (id: string) => void;
  polygonsToEdit?: MapItem[];
  onPolygonsEditCompleted?: (polygons: __esri.Graphic[]) => void;
  onPolygonsEditStarted?: () => void;
  allowMultiplePolygons?: boolean;
  height?: string;
};

const Map = ({
  mapConfig,
  webMapItemId,
  center,
  items,
  showLegend,
  showLayersList,
  onPopupClick,
  polygonsToEdit,
  onPolygonsEditCompleted,
  onPolygonsEditStarted,
  allowMultiplePolygons = true,
  height,
}: Props) => {
  const mapEl = useRef(null);

  const [sketchVm, setSketchVm] = useState<__esri.SketchViewModel>();
  const [currentGraphic, setCurrentGraphic] = useState<__esri.Graphic | null>(null);
  const currentGraphicRef = useRef<__esri.Graphic | null>(null);
  const [graphicsLayer, setGraphicsLayer] = useState<__esri.GraphicsLayer | null>(null);
  const [view, setView] = useState<__esri.MapView | null>(null);
  const [legend, setLegend] = useState<__esri.Legend | null>(null);
  const [layersList, setLayersList] = useState<__esri.LayerList | null>(null);
  const [editInProgress, setEditInProgress] = useState(false);
  const canEdit = !!onPolygonsEditCompleted;

  // Update ref whenever currentGraphic state changes
  useEffect(() => {
    currentGraphicRef.current = currentGraphic;
  }, [currentGraphic]);

  useEffect(() => {
    if (view) {
      if (showLegend) {
        loadModules(["esri/widgets/Legend"]).then(([Legend]) => {
          const legend = new Legend({
            view: view,
          });
          setLegend(legend);
          view.ui.add(legend, "top-right");
        });
      } else if (legend) {
        view.ui.remove(legend);
        setLegend(null);
      }
    }
  }, [view, showLegend]);

  useEffect(() => {
    if (view) {
      if (showLayersList) {
        loadModules(["esri/widgets/LayerList"]).then(([LayerList]) => {
          const layerList = new LayerList({
            view: view,
          });
          view.ui.add(layerList, "top-right");
          setLayersList(layerList);
        });
      } else if (layersList) {
        view.ui.remove(layersList);
        setLayersList(null);
      }
    }
  }, [view, showLayersList]);

  useEffect(() => {
    loadCss();
    loadModules([
      "esri/WebMap",
      "esri/views/MapView",
      "esri/layers/GraphicsLayer",
      "esri/widgets/ScaleBar",
      "esri/widgets/Legend",
      "esri/identity/OAuthInfo",
      "esri/identity/IdentityManager",
      "esri/widgets/BasemapToggle",
      "esri/Basemap",
    ]).then(([WebMap, MapView, GraphicsLayer, ScaleBar, Legend, OAuthInfo, esriId, BasemapToggle, Basemap]) => {
      // config.apiKey =
      //   "AAPKe7d21445ba604a5488756d8cd0d232f4OzbQd3I0wGKfs8gXxfkdow9QldSlgBNDJlyOdMSWpz5iRabyH8rT3-MOgq3t2HHD";
      configAuth(OAuthInfo, esriId, mapConfig.authAppId, mapConfig.portalUrl, "/esri-auth-callback.html");

      if (!webMapItemId) {
        return;
      }

      const { webmap, graphicsLayer } = createWebMap(WebMap, GraphicsLayer, webMapItemId);
      setGraphicsLayer(graphicsLayer);

      var gotoTargets = () => {
        // Filter to get Permit boundary for the Goto target.
        let targets = graphicsLayer.graphics.filter((g) => g.geometry.type === "polygon");

        // If there are no Permit boundary targets, fall back to the Project boundary target, which is a polyline.
        if (targets.length === 0) {
          targets = graphicsLayer.graphics.filter((g) => g.geometry.type === "polyline");
        }

        return targets;
      };

      var view = createMapView(
        MapView,
        ScaleBar,
        Legend,
        mapEl.current,
        webmap,
        showLegend ?? false,
        gotoTargets,
        center,
        onPopupClick
      );
      setView(view);

      let basemap = new Basemap({
        portalItem: {
          id: "86265e5a4bbb4187a59719cf134e0018", // World Imagery
        },
      });

      // 1 - Create the widget
      const toggle = new BasemapToggle({
        // 2 - Set properties
        view: view, // view that provides access to the map's 'topo-vector' basemap
        nextBasemap: basemap, // allows for toggling to the 'hybrid' basemap
      });

      // Add widget to the top right corner of the view
      view?.ui.add(toggle, "bottom-right");
    });

    return () => {
      // clean up the map view
      if (!!view) {
        graphicsLayer?.destroy();
        view.ui?.remove("mainWidget");
        sketchVm?.destroy();
        view.destroy();
        setView(null);
      }
    };
  }, [mapEl]);

  useEffect(() => {
    if (!(graphicsLayer && view && (items || polygonsToEdit))) {
      return;
    }

    loadModules([
      "esri/Graphic",
      "esri/PopupTemplate",
      "esri/geometry/Point",
      "esri/geometry/Polygon",
      "esri/geometry/Polyline",
      "esri/symbols/SimpleMarkerSymbol",
      "esri/symbols/SimpleFillSymbol",
      "esri/symbols/SimpleLineSymbol",
    ]).then(
      ([Graphic, PopupTemplate, Point, Polygon, Polyline, SimpleMarkerSymbol, SimpleFillSymbol, SimpleLineSymbol]) => {
        graphicsLayer.removeAll();

        // Add polygons to the graphics layer
        polygonsToEdit?.forEach((item) => {
          addPolygon(
            SimpleFillSymbol,
            Graphic,
            PopupTemplate,
            graphicsLayer,
            new Polygon(item.geometry),
            item.color,
            item.style,
            item.popup,
            item.attributes
          );
        });

        // Add items to the graphics layer
        items?.forEach((item) => {
          var geometry = item.geometry;

          if (geometry.type === "point") {
            addSimpleMarker(SimpleMarkerSymbol, Graphic, view, new Point(geometry), item.color);
          } else if (geometry.type === "polygon") {
            addPolygon(
              SimpleFillSymbol,
              Graphic,
              PopupTemplate,
              graphicsLayer,
              new Polygon(geometry),
              item.color,
              item.style,
              item.popup
            );
          } else if (geometry.type === "polyline") {
            addPolyline(
              SimpleLineSymbol,
              Graphic,
              PopupTemplate,
              graphicsLayer,
              new Polyline(geometry),
              item.color,
              item.popup
            );
          }
        });
      }
    );
  }, [polygonsToEdit, items, graphicsLayer, view]);

  // Initialize SketchViewModel and selection/delete handlers
  useEffect(() => {
    if (!view || !graphicsLayer) return;

    loadModules(["esri/widgets/Sketch/SketchViewModel", "esri/symbols/SimpleFillSymbol"]).then(
      ([SketchViewModel, SimpleFillSymbol]) => {
        const vm = createSketchView(
          SketchViewModel,
          view,
          graphicsLayer,
          SimpleFillSymbol,
          onDrawingCompleted,
          onDrawingCancelled,
          onEditCompleted
        );
        setSketchVm(vm);

        // Select a graphic on click
        const clickHandle = view.on("click", (event) => {
          view.hitTest(event).then((response) => {
            const result = response.results.find((r) => r.graphic && r.graphic.layer === graphicsLayer);
            if (result) {
              setCurrentGraphic(result.graphic as __esri.Graphic);
            } else {
              setCurrentGraphic(null);
            }
          });
        });

        // Delete only the selected graphic on Delete key
        const deleteHandle = view.on("key-down", (evt) => {
          if (evt.key === "Delete" && currentGraphicRef.current && onPolygonsEditCompleted) {
            graphicsLayer.remove(currentGraphicRef.current);
            setCurrentGraphic(null);
            // fire full array
            const all = graphicsLayer.graphics.filter((g) => g.geometry.type === "polygon").toArray();
            onPolygonsEditCompleted(all);
          }
        });

        return () => {
          clickHandle.remove();
          deleteHandle.remove();
          vm.destroy();
        };
      }
    );
  }, [view, graphicsLayer, onPolygonsEditCompleted]);

  const onDrawingCompleted = () => {
    setCurrentGraphic(null);
    if (graphicsLayer && onPolygonsEditCompleted) {
      // fire full array
      const all = graphicsLayer.graphics.filter((g) => g.geometry.type === "polygon").toArray();
      onPolygonsEditCompleted(all);
    }
  };

  const onDrawingCancelled = () => {
    setCurrentGraphic(null);
  };

  const onEditCompleted = () => {
    setCurrentGraphic(null);
    setEditInProgress(false);
    if (graphicsLayer && onPolygonsEditCompleted) {
      // fire full array
      const all = graphicsLayer.graphics.filter((g) => g.geometry.type === "polygon").toArray();
      onPolygonsEditCompleted(all);
    }
  };

  // Draw a new polygon
  const handleDrawClick = () => {
    if (!sketchVm) return;
    sketchVm.create("polygon");
    setCurrentGraphic(null);
  };

  // Reshape selected polygon
  const handleReshapeClick = () => {
    if (!sketchVm || !currentGraphicRef.current) return;
    sketchVm.update([currentGraphicRef.current], { tool: "reshape", toggleToolOnClick: false });
    setEditInProgress(true);
    onPolygonsEditStarted && onPolygonsEditStarted();
  };

  // Transform selected polygon
  const handleTransformClick = () => {
    if (!sketchVm || !currentGraphicRef.current) return;
    sketchVm.update([currentGraphicRef.current], { tool: "transform", toggleToolOnClick: false });
    setEditInProgress(true);
    onPolygonsEditStarted && onPolygonsEditStarted();
  };

  return (
    <>
      <div className="webmap" ref={mapEl} style={{ height: height ? height : "700px" }}>
        <div id="BasemapToggle"></div>
        {canEdit && (
          <div id="mainWidget" className="esri-widget">
            <Button.Group basic size="tiny">
              <Button
                id="drawButton"
                onClick={handleDrawClick}
                disabled={(!allowMultiplePolygons && graphicsLayer?.graphics?.length === 1) || editInProgress}
              >
                Draw boundaries
              </Button>
              <Button id="reshapeButton" onClick={handleReshapeClick} disabled={!currentGraphic || editInProgress}>
                Reshape
              </Button>
              <Button id="transformButton" onClick={handleTransformClick} disabled={!currentGraphic || editInProgress}>
                Transform
              </Button>
            </Button.Group>
          </div>
        )}
      </div>
    </>
  );
};

export default Map;
